// MatchContext.js
import React, { createContext, useContext, useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { produce } from 'immer'

const MatchContext = createContext()
const STORAGE_KEY = 'gully_scoreboard_v1'
const HISTORY_MAX = 1000 // cap history to keep memory bounded

export function useMatch() {
    return useContext(MatchContext)
}

function defaultState() {
    return { match: null }
}

export function MatchProvider({ children }) {
    const [state, setState] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY)
            return raw ? JSON.parse(raw) : defaultState()
        } catch (e) {
            console.error('Failed read localStorage', e)
            return defaultState()
        }
    })

    // throttle/debounce localStorage writes and persist on unload
    const persistTimer = useRef(null)
    useEffect(() => {
        if (persistTimer.current) clearTimeout(persistTimer.current)
        persistTimer.current = setTimeout(() => {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
            } catch (e) {
                console.error('Failed saving state', e)
            }
        }, 800) // write at most every 800ms
        return () => clearTimeout(persistTimer.current)
    }, [state])

    useEffect(() => {
        const onUnload = () => {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
            } catch (e) { }
        }
        window.addEventListener('beforeunload', onUnload)
        return () => window.removeEventListener('beforeunload', onUnload)
    }, [state])

    const startMatch = useCallback((match) => {
        setState({ match })
    }, [])

    // updateMatch accepts a mutating function that receives a draft match (immer)
    const updateMatch = useCallback((mutator) => {
        setState(prev => {
            if (!prev.match) return prev
            const next = produce(prev, draft => {
                // mutator can mutate draft.match
                const res = mutator(draft.match)
                // allow mutator to optionally return something; ignore otherwise
            })
            // ensure history capped
            if (next.match?.history && next.match.history.length > HISTORY_MAX) {
                next.match.history = next.match.history.slice(-HISTORY_MAX)
            }
            return next
        })
    }, [])

    const resetMatch = useCallback(() => setState(defaultState()), [])

    const value = useMemo(() => ({ state, startMatch, updateMatch, resetMatch }), [state, startMatch, updateMatch, resetMatch])

    return <MatchContext.Provider value={value}>{children}</MatchContext.Provider>
}
